import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { useVirtualizer } from '@tanstack/react-virtual'
import { DateTime } from 'luxon'
import {
  CalendarDays,
  ChevronRight,
  FolderInput,
  ListChecks,
  ListTree,
  Loader2,
  MoreHorizontal,
  UserPlus,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { toast } from 'sonner'

import { useTranslation } from 'react-i18next'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { formatShortAge, getDueDateColorClass } from '../utils/datetime'
import { extractPlainTextFromTiptap } from '../utils/editorUtils'
import { resolveTaskIdentifier } from '../utils/taskIdentifier'
import { useProjects } from '../hooks/useProjects'
import { useTaskRowKeys } from '../hooks/useTaskRowKeys'
import { isClosedProgressType } from '../constants'
import { useListTaskRowCounts } from './TaskRowCountsProvider'
import { TaskRowAssigneePicker, TaskRowDatePicker, TaskRowProjectPicker } from './TaskActionMenus'
import { PriorityGlyph } from './PriorityComboBox'
import { TaskPrIndicator } from './TaskPrIndicator'
import { TaskEmptyState } from './TaskEmptyState'

import type { Dispatch, KeyboardEvent, ReactNode, SetStateAction } from 'react'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { EnrichedTask } from '@/lib/db/rxdb/hooks/entities/tasks'
import type { TaskRowCounts } from '../hooks/useTaskRowCounts'
import type { CustomFieldDefinitionDocument, CustomFieldValueDocument } from '@/components/features/custom-fields/types'
import type { StatusMode } from '@/hooks/useProjectStatusMode'
import type { WorkItemTypeDocument } from '@/lib/db/rxdb/hooks/entities/work-item-types'
import type { RowMenu } from '@/store/TaskRowActionsStore'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import useViewModeStore from '@/store/ViewModeStore'
import { useTaskSelectionStore } from '@/store/TaskSelectionStore'
import { TaskStatusCheckbox } from '@/components/app/tasks/TaskStatusCheckbox'
import { cn } from '@/lib/utils'
import { useTaskContent, useTaskMutations } from '@/lib/db/rxdb/hooks'
import { useProjectStatusModeMap } from '@/hooks/useProjectStatusModeMap'
import { createDragExtractor } from '@/lib/utils/dragDrop'
import { useTaskRowActionsStore } from '@/store/TaskRowActionsStore'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { useWorkItemTypes } from '@/lib/db/rxdb/hooks/entities/work-item-types'
import { resolveDefaultWorkItemTypeId } from '@/lib/types/workItemType'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { WorkItemTypeIcon } from '@/components/app/tasks/WorkItemTypeIcon'
import { FIELD_TYPE_REGISTRY } from '@/components/features/custom-fields/registry'
import { parseOptions } from '@/components/features/custom-fields/types'
import { useIsMobile } from '@/hooks/useMobile'

export type TaskListSection = {
  id: string
  header: ReactNode
  headerClassName?: string
  itemIds: Array<string>
}

type DropIndicatorState =
  | {
      type: 'item'
      id: string
      position: 'before' | 'after'
    }
  | {
      type: 'container'
      position: 'end'
    }
  | null

const extractTaskDragData = createDragExtractor('task')

export function formatTaskDueDate(
  value: TaskDocument['dueDate'] | Date | string | null | undefined,
  baseDate?: Date,
) {
  if (!value) {
    return ''
  }

  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const target = DateTime.fromJSDate(date)
  const base = baseDate ? DateTime.fromJSDate(baseDate) : DateTime.now()

  if (!target.isValid || !base.isValid) {
    return ''
  }

  const includeYear = !target.hasSame(base, 'year')
  return target.toFormat(includeYear ? 'MMM d, yyyy' : 'MMM d')
}

export type CustomFieldColumnData = {
  definitions: Array<CustomFieldDefinitionDocument>
  valueMap: Map<string, Map<string, CustomFieldValueDocument>>
}

type TaskListProps = {
  items: Array<TaskDocument>
  className?: string
  style?: React.CSSProperties
  itemClassName?: string
  formatDueDate?: (value: TaskDocument['dueDate']) => string
  onReorder?: (event: TaskReorderEvent) => void
  onSelect?: (task: TaskDocument) => void
  selectedTaskId?: string | null
  isLoading?: boolean
  sections?: Array<TaskListSection>
  virtualized?: boolean
  viewMode?: 'simple' | 'split'
  /** Whether to show empty state when items.length === 0 (default: true) */
  showEmptyState?: boolean
  /** Custom field column data (batch-loaded at parent level) */
  customFieldColumns?: CustomFieldColumnData
  /** Rendered as the last row inside the card, e.g. "Show N more". */
  footer?: React.ReactNode
  /** Extra metadata before the ID, e.g. a project chip (design 34a). */
  renderRowMeta?: (task: TaskDocument) => ReactNode
  /** Due text from the whole task, overriding `formatDueDate` (design 34a). */
  formatTaskDue?: (task: TaskDocument) => string
  /**
   * A fixed ID column width shared across several lists (design 34a: IDs sit
   * in one right-hand column under every day).
   */
  idColumnWidthCh?: number
}

export type TaskReorderEvent =
  | {
      type: 'item'
      sourceId: string
      targetId: string
      dropPosition: 'before' | 'after'
      sourceSectionId?: string
      targetSectionId?: string
    }
  | {
      type: 'container'
      sourceId: string
      dropPosition: 'end'
      sourceSectionId?: string
      targetSectionId?: string
    }

type RenderRow =
  | {
      type: 'section'
      section: TaskListSection
    }
  | {
      type: 'task'
      item: TaskDocument
      section: TaskListSection | null
      isFirstInSection: boolean
      isLastInSection: boolean
    }
  | {
      /** A sub-task shown under its expanded parent (design 28b). */
      type: 'subtask'
      item: EnrichedTask
      section: TaskListSection | null
    }

export function TaskList({
  items,
  className,
  style,
  itemClassName,
  formatDueDate = formatTaskDueDate,
  onReorder,
  onSelect,
  selectedTaskId,
  isLoading = false,
  sections = [],
  virtualized = true,
  viewMode = 'split',
  showEmptyState = true,
  customFieldColumns,
  footer,
  renderRowMeta,
  formatTaskDue,
  idColumnWidthCh,
}: TaskListProps) {
  const { updateTask } = useTaskMutations()
  const { isPersonal } = useWorkspaceMode()
  const { statusModeMap } = useProjectStatusModeMap()
  const { projects } = useProjects()
  const { workItemTypes: allWorkItemTypes } = useWorkItemTypes({ isDeleted: false })

  useTaskRowKeys(items)

  const workItemTypeEnabledByProject = useMemo(() => {
    const map = new Map<string, boolean>()
    projects.forEach((p) =>
      map.set(p.id, FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES && (p.isWorkItemTypeEnabled ?? false)),
    )
    return map
  }, [projects])

  const projectKeyById = useMemo(() => {
    const map = new Map<string, string>()
    projects.forEach((p) => {
      if (p.projectKey) map.set(p.id, p.projectKey)
    })
    return map
  }, [projects])

  const resolveIdentifier = useCallback(
    (task: TaskDocument): string =>
      resolveTaskIdentifier(
        task,
        task.projectId ? projectKeyById.get(task.projectId) : null,
      ),
    [projectKeyById],
  )

  const { t } = useTranslation('tasks')
  const listDensity = useViewModeStore((s) => s.listDensity)

  // One ID column width for the whole list (0 = no IDs at all). The 7ch floor
  // keeps typical IDs (ZYR-15, START-4) aligned across separately rendered
  // groups; longer ones widen it.
  const computedIdColumnCh = useMemo(() => {
    const longest = items.reduce((max, task) => Math.max(max, resolveIdentifier(task).length), 0)
    return longest === 0 ? 0 : Math.max(7, longest)
  }, [items, resolveIdentifier])
  const idColumnCh = idColumnWidthCh ?? computedIdColumnCh
  const statusModeFor = useCallback(
    (task: TaskDocument): StatusMode =>
      isPersonal ? 'simple' : statusModeMap.get(task.projectId ?? '') ?? 'simple',
    [isPersonal, statusModeMap],
  )

  const workItemTypeById = useMemo(() => {
    const map = new Map<string, WorkItemTypeDocument>()
    allWorkItemTypes.forEach((type) => map.set(type.id, type))
    return map
  }, [allWorkItemTypes])

  const defaultWorkItemTypeId = useMemo(
    () => resolveDefaultWorkItemTypeId(allWorkItemTypes),
    [allWorkItemTypes],
  )

  const resolveWorkItemType = useCallback(
    (task: TaskDocument): WorkItemTypeDocument | null => {
      if (isPersonal) return null
      const projectId = task.projectId
      if (!projectId) return null
      if (!workItemTypeEnabledByProject.get(projectId)) return null
      const typeId = task.workItemTypeId ?? defaultWorkItemTypeId
      return typeId ? workItemTypeById.get(typeId) ?? null : null
    },
    [isPersonal, workItemTypeEnabledByProject, workItemTypeById, defaultWorkItemTypeId],
  )
  const parentRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndicator, setDropIndicator] = useState<DropIndicatorState>(null)

  // The parent in a list (design 28b): counts on the row, sub-tasks one click
  // away. Collapsed by default; Display → Show sub-tasks flattens them into
  // the list instead, so parents need no chevron then.
  const showSubTasks = useViewModeStore((s) => s.showSubTasks)
  const { countsByTask, subTasksByParent } = useListTaskRowCounts()
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set())
  const toggleExpanded = useCallback((taskId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }, [])
  const expandableIds = useMemo(() => {
    const ids = new Set<string>()
    if (showSubTasks) return ids
    const itemIds = new Set(items.map((item) => item.id))
    for (const item of items) {
      const subTasks = subTasksByParent.get(item.id)
      // A list that already has these sub-tasks as rows (Someday) has nothing
      // to expand.
      if (subTasks?.length && !subTasks.some((st) => itemIds.has(st.id))) ids.add(item.id)
    }
    return ids
  }, [items, showSubTasks, subTasksByParent])

  const rows = useMemo<Array<RenderRow>>(() => {
    const withSubTasks = (list: Array<RenderRow>): Array<RenderRow> =>
      list.flatMap((row) => {
        if (row.type !== 'task' || !expandedIds.has(row.item.id) || !expandableIds.has(row.item.id)) {
          return [row]
        }
        const subTasks = subTasksByParent.get(row.item.id) ?? []
        return [
          row,
          ...subTasks.map((item): RenderRow => ({ type: 'subtask', item, section: row.section })),
        ]
      })

    if (sections.length === 0) {
      return withSubTasks(
        items.map((item, index) => ({
          type: 'task' as const,
          item,
          section: null,
          isFirstInSection: index === 0,
          isLastInSection: index === items.length - 1,
        })),
      )
    }

    const itemMap = new Map(items.map((item) => [item.id, item]))
    const usedIds = new Set<string>()
    const computed: Array<RenderRow> = []

    sections.forEach((section) => {
      const sectionItems = section.itemIds
        .map((itemId) => itemMap.get(itemId))
        .filter((value): value is TaskDocument => Boolean(value))

      if (sectionItems.length === 0) {
        return
      }

      computed.push({ type: 'section', section })

      sectionItems.forEach((sectionItem, index) => {
        usedIds.add(sectionItem.id)
        computed.push({
          type: 'task',
          item: sectionItem,
          section,
          isFirstInSection: index === 0,
          isLastInSection: index === sectionItems.length - 1,
        })
      })
    })

    const remainingItems = items.filter((item) => !usedIds.has(item.id))
    if (remainingItems.length === 0) {
      return withSubTasks(computed)
    }

    remainingItems.forEach((item, index) => {
      computed.push({
        type: 'task',
        item,
        section: null,
        isFirstInSection:
          computed.length === 0 ||
          computed[computed.length - 1]?.type === 'section',
        isLastInSection: index === remainingItems.length - 1,
      })
    })

    return withSubTasks(computed)
  }, [items, sections, expandedIds, expandableIds, subTasksByParent])
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const type = rows[index]?.type
      return type === 'section' ? 64 : type === 'subtask' ? 36 : 48
    },
    overscan: 12,
  })

  useEffect(() => {
    if (!onReorder) {
      return
    }

    const element = listRef.current
    if (!element) {
      return
    }

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => Boolean(extractTaskDragData(source.data)),
      getData: () => ({ type: 'container' }),
      onDropTargetChange: ({ location, self }) => {
        const innermost = location.current.dropTargets[0]

        if (innermost?.element === self.element) {
          setDropIndicator({ type: 'container', position: 'end' })
        } else {
          setDropIndicator((current) =>
            current?.type === 'container' ? null : current,
          )
        }
      },
      onDragLeave: () => {
        setDropIndicator((current) =>
          current?.type === 'container' ? null : current,
        )
      },
      onDrop: ({ location, source, self }) => {
        const taskData = extractTaskDragData(source.data)
        setDropIndicator(null)
        if (!taskData) {
          return
        }

        const innermostTarget = location.current.dropTargets[0]
        if (innermostTarget?.element !== self.element) {
          return
        }

        // Find source section ID
        const sourceElement = document.querySelector(
          `[data-task-id="${taskData.id}"]`,
        )
        const sourceSectionId =
          sourceElement?.getAttribute('data-section-id') || undefined

        onReorder({
          type: 'container',
          sourceId: taskData.id,
          dropPosition: 'end',
          sourceSectionId,
        })
        setDropIndicator(null)
      },
    })
  }, [onReorder, setDropIndicator])

  if (isLoading) {
    return (
      <div
        className={cn(
          'relative flex min-h-[240px] items-center justify-center bg-background',
          className,
        )}
      >
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span>Loading tasks...</span>
        </div>
      </div>
    )
  }

  if (items.length === 0 && showEmptyState) {
    return <TaskEmptyState className={className} />
  }

  // Compact density gets a real column header (design 13b).
  const hasTaskRows = rows.some((row) => row.type === 'task')
  const compactHeader =
    listDensity === 'compact' && hasTaskRows ? (
            <li
              aria-hidden
              className="flex items-center gap-1 border-b bg-secondary/40 py-1.5 pl-2 pr-3 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground @2xl:pl-3 @2xl:pr-4"
            >
              <span className="w-[6.5rem] shrink-0" />
              <span className="flex-1">{t('columns.title')}</span>
              {idColumnCh > 0 && <span className="shrink-0" style={{ width: `${idColumnCh}ch` }}>{t('columns.id')}</span>}
              <span className="w-14 shrink-0">{t('columns.due')}</span>
              <span className="w-9 shrink-0 text-right">{t('columns.age')}</span>
            </li>
    ) : null

  if (!virtualized) {
    let sectionCount = 0
    return (
      <div
        ref={parentRef}
        className={cn(
          // Rows size themselves against this element, not the viewport — in
          // split view the pane is far narrower than the window (MOL-11).
          // Height comes from the flex parent (`flex-1 min-h-0`), never from
          // viewport math: this is the only scroll container in the column.
          '@container relative bg-background overflow-y-auto',
          className,
        )}
        style={style}
      >
        {/* Rows sit on a white card inside the paper canvas (design 6a). */}
        <ul
          ref={listRef}
          className={cn(
            'mx-3 mb-4 flex flex-col @2xl:mx-4',
            rows.some((row) => row.type === 'task') &&
              'overflow-hidden rounded-lg border bg-card',
          )}
        >
          {compactHeader}
          {rows.map((row, index) => {
            if (row.type === 'subtask') {
              return (
                <SubTaskListRow
                  key={`subtask-${row.item.id}`}
                  item={row.item}
                  virtualized={false}
                  onSelect={onSelect}
                  isSelected={selectedTaskId === row.item.id}
                  updateTask={updateTask}
                  statusMode={statusModeFor(row.item)}
                  taskIdentifier={resolveIdentifier(row.item)}
                  idColumnCh={idColumnCh}
                  formatDueDate={formatDueDate}
                  viewMode={viewMode}
                />
              )
            }
            if (row.type === 'section') {
              const isFirstSection = sectionCount === 0
              sectionCount += 1
              return (
                <TaskListSectionHeader
                  key={`section-${row.section.id}`}
                  section={row.section}
                  isFirstSection={isFirstSection}
                  virtualized={false}
                />
              )
            }

            return (
              <TaskListItem
                key={row.item.id}
                item={row.item}
                index={index}
                isFirst={row.isFirstInSection}
                itemClassName={itemClassName}
                formatDueDate={formatDueDate}
                draggingId={draggingId}
                setDraggingId={setDraggingId}
                onReorder={onReorder}
                dropIndicator={dropIndicator}
                setDropIndicator={setDropIndicator}
                onSelect={onSelect}
                isSelected={selectedTaskId === row.item.id}
                section={row.section}
                virtualized={false}
                updateTask={updateTask}
                viewMode={viewMode}
                customFieldColumns={isPersonal ? undefined : customFieldColumns}
                statusMode={isPersonal ? 'simple' : statusModeMap.get(row.item.projectId ?? '') ?? 'simple'}
                workItemType={resolveWorkItemType(row.item)}
                taskIdentifier={resolveIdentifier(row.item)}
                idColumnCh={idColumnCh}
                fixedIdColumn={idColumnWidthCh !== undefined}
                rowMeta={renderRowMeta?.(row.item)}
                dueText={formatTaskDue?.(row.item)}
                counts={countsByTask.get(row.item.id)}
                expandable={expandableIds.has(row.item.id)}
                expanded={expandedIds.has(row.item.id)}
                onToggleExpanded={toggleExpanded}
              />
            )
          })}
          {dropIndicator?.type === 'container' && (
            <li
              key="container-drop-indicator"
              className="pointer-events-none mx-4 h-0.5 rounded bg-primary"
              aria-hidden="true"
            />
          )}
          {footer && hasTaskRows && <li>{footer}</li>}
        </ul>
      </div>
    )
  }

  const virtualItems = rowVirtualizer.getVirtualItems()

  return (
    <div
      ref={parentRef}
      className={cn(
        // Height comes from the flex parent (`flex-1 min-h-0`), never from
        // viewport math: this is the only scroll container in the column.
        '@container relative bg-background overflow-y-auto',
        className,
      )}
      style={style}
    >
      <div className={cn(hasTaskRows && 'mx-3 mb-4 overflow-hidden rounded-lg border bg-card @2xl:mx-4')}>
      {compactHeader && <ul>{compactHeader}</ul>}
      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        <ul
          ref={listRef}
          className="absolute inset-x-0 top-0"
          style={{ height: rowVirtualizer.getTotalSize() }}
        >
          <AnimatePresence initial={false}>
            {virtualItems.map((virtualItem) => {
              const row = rows[virtualItem.index]
              // The virtualizer can briefly report an index past the end while
              // rows are being recomputed. Bailing here also lets the discriminated
              // union narrow on `row.type` below — without it every `row.item`
              // access fails, because `RenderRow | undefined` cannot discriminate.
              if (!row) return null
              if (row.type === 'subtask') {
                return (
                  <SubTaskListRow
                    key={`subtask-${row.item.id}`}
                    item={row.item}
                    index={virtualItem.index}
                    offset={virtualItem.start}
                    measureElement={rowVirtualizer.measureElement}
                    virtualized
                    onSelect={onSelect}
                    isSelected={selectedTaskId === row.item.id}
                    updateTask={updateTask}
                    statusMode={statusModeFor(row.item)}
                    taskIdentifier={resolveIdentifier(row.item)}
                    idColumnCh={idColumnCh}
                    formatDueDate={formatDueDate}
                    viewMode={viewMode}
                  />
                )
              }
              if (row.type === 'section') {
                return (
                  <TaskListSectionHeader
                    key={`section-${row.section.id}`}
                    section={row.section}
                    offset={virtualItem.start}
                    measureElement={rowVirtualizer.measureElement}
                    isFirstSection={virtualItem.index === 0}
                    virtualized
                  />
                )
              }

              return (
                <TaskListItem
                  key={row.item.id}
                  item={row.item}
                  index={virtualItem.index}
                  offset={virtualItem.start}
                  isFirst={row.isFirstInSection}
                  measureElement={rowVirtualizer.measureElement}
                  itemClassName={itemClassName}
                  formatDueDate={formatDueDate}
                  draggingId={draggingId}
                  setDraggingId={setDraggingId}
                  onReorder={onReorder}
                  dropIndicator={dropIndicator}
                  setDropIndicator={setDropIndicator}
                  onSelect={onSelect}
                  isSelected={selectedTaskId === row.item.id}
                  section={row.section}
                  virtualized
                  updateTask={updateTask}
                  viewMode={viewMode}
                  statusMode={isPersonal ? 'simple' : statusModeMap.get(row.item.projectId ?? '') ?? 'simple'}
                  workItemType={resolveWorkItemType(row.item)}
                  taskIdentifier={resolveIdentifier(row.item)}
                  idColumnCh={idColumnCh}
                  fixedIdColumn={idColumnWidthCh !== undefined}
                  rowMeta={renderRowMeta?.(row.item)}
                  dueText={formatTaskDue?.(row.item)}
                  counts={countsByTask.get(row.item.id)}
                  expandable={expandableIds.has(row.item.id)}
                  expanded={expandedIds.has(row.item.id)}
                  onToggleExpanded={toggleExpanded}
                />
              )
            })}
          </AnimatePresence>
        </ul>
        {dropIndicator?.type === 'container' && (
          <div
            className="pointer-events-none absolute left-4 right-4 h-0.5 rounded bg-primary"
            style={{ top: rowVirtualizer.getTotalSize() - 2 }}
          />
        )}
      </div>
      {footer && hasTaskRows && footer}
      </div>
    </div>
  )
}

type TaskListSectionHeaderProps = {
  section: TaskListSection
  offset?: number
  measureElement?: (element: Element | null) => void
  isFirstSection: boolean
  virtualized: boolean
}

function TaskListSectionHeader({
  section,
  offset = 0,
  measureElement,
  isFirstSection,
  virtualized,
}: TaskListSectionHeaderProps) {
  const measureElementRef = useRef(measureElement)
  measureElementRef.current = measureElement
  const setRef = useCallback((node: HTMLLIElement | null) => {
    measureElementRef.current?.(node)
  }, [])

  const spacingClasses = isFirstSection ? 'pt-0' : 'pt-4'
  if (virtualized) {
    return (
      <motion.li
        ref={setRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          transform: `translateY(${offset}px)`,
        }}
        className={cn('bg-background px-4 @2xl:px-8', spacingClasses, section.headerClassName)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        {section.header}
      </motion.li>
    )
  }

  return (
    <motion.li
      ref={setRef}
      className={cn('bg-background ', spacingClasses, section.headerClassName)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      {section.header}
    </motion.li>
  )
}

type TaskListItemProps = {
  item: TaskDocument
  index: number
  offset?: number
  isFirst: boolean
  measureElement?: (element: Element | null) => void
  itemClassName?: string
  formatDueDate: (value: TaskDocument['dueDate']) => string
  draggingId: string | null
  setDraggingId: (id: string | null) => void
  onReorder?: (event: TaskReorderEvent) => void
  dropIndicator: DropIndicatorState
  setDropIndicator: Dispatch<SetStateAction<DropIndicatorState>>
  onSelect?: (task: TaskDocument) => void
  isSelected: boolean
  section: TaskListSection | null
  virtualized: boolean
  updateTask: (id: string, updates: any) => Promise<void>
  viewMode: 'simple' | 'split'
  customFieldColumns?: CustomFieldColumnData
  statusMode: StatusMode
  workItemType: WorkItemTypeDocument | null
  taskIdentifier?: string
  /**
   * Width (in ch) of the ID column, or 0 for none. Every row in a list shares
   * it, so titles start at the same x whether or not a row has an ID (13a).
   */
  idColumnCh?: number
  /** Always reserve `idColumnCh`, even on rows without an ID (design 34a). */
  fixedIdColumn?: boolean
  rowMeta?: ReactNode
  /** Replaces `formatDueDate(item.dueDate)` when set. */
  dueText?: string
  /** Checklist and sub-task counts after the title (design 28b). */
  counts?: TaskRowCounts
  /** Whether the sub-task count doubles as the expand chevron. */
  expandable?: boolean
  expanded?: boolean
  onToggleExpanded?: (taskId: string) => void
}

function TaskListItem({
  item,
  index,
  offset = 0,
  isFirst,
  measureElement,
  itemClassName,
  formatDueDate,
  draggingId,
  setDraggingId,
  onReorder,
  dropIndicator,
  setDropIndicator,
  onSelect,
  isSelected,
  section,
  virtualized,
  updateTask,
  viewMode,
  customFieldColumns,
  statusMode,
  workItemType,
  taskIdentifier,
  idColumnCh = 0,
  fixedIdColumn = false,
  rowMeta,
  dueText,
  counts,
  expandable = false,
  expanded = false,
  onToggleExpanded,
}: TaskListItemProps) {
  const itemRef = useRef<HTMLLIElement | null>(null)

  const handleStatusChange = useCallback(
    async (statusId: string) => {
      const nextStatusId = statusId || null
      const currentStatusId = item.statusId || null
      if (currentStatusId === nextStatusId) {
        return
      }

      try {
        await updateTask(item.id, { statusId: nextStatusId })
      } catch (error) {
        toast.error('Failed to update task status')
      }
    },
    [item.id, item.statusId, updateTask],
  )

  const handlePriorityChange = useCallback(
    async (priority: number) => {
      const currentPriority = item.priority ?? 0
      if (currentPriority === priority) {
        return
      }

      try {
        await updateTask(item.id, { priority })
      } catch (error) {
        // Error handling
      }
    },
    [item.id, item.priority, updateTask],
  )

  const handleSelect = useCallback(() => {
    if (draggingId) {
      return
    }

    onSelect?.(item)
  }, [draggingId, item, onSelect])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLLIElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleSelect()
      }
    },
    [handleSelect],
  )

  const measureElementRef = useRef(measureElement)
  measureElementRef.current = measureElement
  const setRef = useCallback((node: HTMLLIElement | null) => {
    itemRef.current = node
    measureElementRef.current?.(node)
  }, [])

  const shouldLoadDescription = viewMode === 'simple'
  const { content: workItemContent } = useTaskContent(
    shouldLoadDescription ? item.id : undefined,
  )

  const descriptionPreview = useMemo(() => {
    if (!shouldLoadDescription) {
      return ''
    }

    const plainText =
      workItemContent?.plainText ??
      (workItemContent?.content
        ? extractPlainTextFromTiptap(workItemContent.content)
        : '')

    return plainText.replace(/\s+/g, ' ').trim()
  }, [
    shouldLoadDescription,
    workItemContent?.plainText,
    workItemContent?.content,
  ])

  const density = useViewModeStore((s) => s.listDensity)
  const narrow = viewMode === 'split'
  const isBulkSelected = useTaskSelectionStore((s) => s.selectedIds.has(item.id))
  const isSelecting = useTaskSelectionStore((s) => s.selectedIds.size > 0)
  const toggleBulk = useTaskSelectionStore((s) => s.toggle)
  const showBox = isBulkSelected || isSelecting
  const setHoveredRow = useTaskRowActionsStore((s) => s.setHovered)
  const clearHoveredRow = useTaskRowActionsStore((s) => s.clearHovered)
  const { isPersonal: isPersonalWorkspace } = useWorkspaceMode()
  // Compact rows are one line with no description (design 13b).
  // Display → Show on rows (design 25b).
  const hiddenFields = useViewModeStore((s) => s.hiddenRowFields)
  const showId = !hiddenFields.includes('id')
  const showDue = !hiddenFields.includes('due')
  const showPriority = !hiddenFields.includes('priority')
  // Phones (design 36a): two-line rows — title, then ID · due · counts —
  // no hover actions and no drag; the row opens the task, the circle completes it.
  const isPhone = useIsMobile()
  const showDescription =
    !isPhone && density === 'comfortable' && !hiddenFields.includes('description') && descriptionPreview.length > 0

  useEffect(() => {
    const element = itemRef.current
    if (!element || isPhone) {
      return
    }
    // The whole row drags (design 23); the grip only appears on the lifted copy.
    const onGenerateDragPreview: Parameters<typeof draggable>[0]['onGenerateDragPreview'] = ({ nativeSetDragImage }) => {
      setCustomNativeDragPreview({
        nativeSetDragImage,
        getOffset: pointerOutsideOfPreview({ x: '12px', y: '8px' }),
        render: ({ container }) => {
          const card = document.createElement('div')
          card.className =
            'flex max-w-[420px] items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm text-foreground shadow-lg'
          const grip = document.createElement('span')
          grip.textContent = '⠿'
          grip.className = 'text-muted-foreground'
          const title = document.createElement('span')
          title.className = 'truncate'
          title.textContent = item.name || 'Untitled task'
          card.append(grip, title)
          container.append(card)
        },
      })
    }

    if (!onReorder) {
      return draggable({
        element,
        onGenerateDragPreview,
        getInitialData: () => ({ type: 'task', id: item.id }),
        onDragStart: () => {
          setDraggingId(item.id)
          setDropIndicator(null)
        },
        onDrop: () => {
          setDraggingId(null)
          setDropIndicator(null)
        },
      })
    }

    return combine(
      draggable({
        element,
        onGenerateDragPreview,
        getInitialData: () => ({ type: 'task', id: item.id }),
        onDragStart: () => {
          setDraggingId(item.id)
          setDropIndicator(null)
        },
        onDrop: () => {
          setDraggingId(null)
          setDropIndicator(null)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => Boolean(extractTaskDragData(source.data)),
        getData: () => ({ type: 'task', id: item.id, index }),
        onDropTargetChange: ({ location, source, self }) => {
          const taskData = extractTaskDragData(source.data)
          if (!taskData || taskData.id === item.id) {
            setDropIndicator((current) =>
              current?.type === 'item' && current.id === item.id
                ? null
                : current,
            )
            return
          }

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          setDropIndicator({
            type: 'item',
            id: item.id,
            position: shouldPlaceAfter ? 'after' : 'before',
          })
        },
        onDragLeave: () => {
          setDropIndicator((current) =>
            current?.type === 'item' && current.id === item.id ? null : current,
          )
        },
        onDrop: ({ location, source, self }) => {
          const taskData = extractTaskDragData(source.data)
          setDropIndicator(null)
          if (!taskData || taskData.id === item.id) {
            return
          }

          const innermostTarget = location.current.dropTargets[0]
          if (!innermostTarget || innermostTarget.element !== self.element) {
            return
          }

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          // Find source section ID
          const sourceElement = document.querySelector(
            `[data-task-id="${taskData.id}"]`,
          )
          const sourceSectionId =
            sourceElement?.getAttribute('data-section-id') || undefined
          const targetSectionId = section?.id

          onReorder({
            type: 'item',
            sourceId: taskData.id,
            targetId: item.id,
            dropPosition: shouldPlaceAfter ? 'after' : 'before',
            sourceSectionId,
            targetSectionId,
          })
          setDropIndicator(null)
        },
      }),
    )
  }, [index, item.id, onReorder, setDraggingId, setDropIndicator, isPhone])

  const isDragging = draggingId === item.id
  const showIndicatorBefore =
    dropIndicator?.type === 'item' &&
    dropIndicator.id === item.id &&
    dropIndicator.position === 'before'
  const showIndicatorAfter =
    dropIndicator?.type === 'item' &&
    dropIndicator.id === item.id &&
    dropIndicator.position === 'after'

  return (
    <motion.li
      data-testid="task-item"
      data-task-id={item.id}
      data-index={index}
      data-section-id={section?.id ?? undefined}
      ref={setRef}
      className={cn(
        // --row-bg drives both the row and the hover-actions fade, so the fade
        // always matches whatever tint the row has.
        'group relative flex items-center gap-1 pl-2 @2xl:pl-3 pr-3 @2xl:pr-4 transition-colors cursor-pointer bg-[var(--row-bg)] [--row-bg:var(--card)] hover:[--row-bg:color-mix(in_oklab,var(--secondary)_40%,var(--card))]',
        density === 'compact' ? 'py-1' : narrow ? 'py-[7px]' : 'py-2.5',
        !isFirst && 'border-t border-border',
        isDragging && 'z-10 shadow-md ring-1 ring-border',
        // Selected: a terracotta tint, no edge bar (design 34a).
        isSelected &&
          viewMode === 'split' &&
          '[--row-bg:color-mix(in_oklab,var(--brand-tint)_45%,var(--card))] hover:[--row-bg:color-mix(in_oklab,var(--brand-tint)_60%,var(--card))]',
        isBulkSelected && '[--row-bg:color-mix(in_oklab,var(--brand-tint)_35%,var(--card))] hover:[--row-bg:color-mix(in_oklab,var(--brand-tint)_45%,var(--card))]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
        itemClassName,
      )}
      style={
        virtualized
          ? {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${offset}px)`,
              zIndex: isDragging ? 10 : 1,
            }
          : undefined
      }
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      role="button"
      tabIndex={0}
      aria-selected={isSelected}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHoveredRow(item.id)}
      onMouseLeave={() => clearHoveredRow(item.id)}
    >
      {showIndicatorBefore && (
        <div className="pointer-events-none absolute left-0 right-0 top-0 h-0.5 rounded bg-brand" />
      )}
      {/* One leading slot (design 23): priority at rest, the bulk checkbox on
          hover, and on every row once anything is selected. No drag handle —
          the whole row drags. */}
      <div
        className="relative flex size-5 shrink-0 items-center justify-center phone:hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <span
          className={cn(
            'flex items-center justify-center transition-opacity',
            showBox ? 'opacity-0' : 'group-hover:opacity-0 group-focus-within:opacity-0',
          )}
        >
          {showPriority && <PriorityGlyph priority={item.priority ?? 0} />}
        </span>
        {/* Radix, not a native input: Chrome darkens and rings a hovered
            accent-color checkbox, so the row under the cursor looked off. */}
        <Checkbox
          checked={isBulkSelected}
          onCheckedChange={() => toggleBulk(item.id)}
          aria-label={item.name || 'Untitled task'}
          className={cn(
            'absolute size-3.5 cursor-pointer rounded-[3px] border-muted-foreground/50 transition-opacity data-[state=checked]:border-[var(--brand)] data-[state=checked]:bg-[var(--brand)] data-[state=checked]:text-white [&_svg]:size-2.5',
            showBox ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
          )}
        />
      </div>
      <div
        className="flex h-6 w-6 items-center justify-center phone:-ml-2 phone:size-10 phone:self-start phone:-mt-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <TaskStatusCheckbox
          currentStatusId={item.statusId}
          onStatusChange={handleStatusChange}
          statusMode={statusMode}
          className="h-5 w-5"
        />
      </div>
      {workItemType ? (
        <WorkItemTypeIcon
          icon={workItemType.icon}
          color={workItemType.color}
          className="size-4 shrink-0"
          aria-label={workItemType.name}
        />
      ) : null}
      {/* Title, then the description muted on the same line: every row is one
          line tall (design 13a). */}
      <div className="flex min-w-0 flex-1 items-baseline gap-2 phone:flex-col phone:items-start phone:gap-0.5">
        <span
          className="min-w-0 shrink truncate text-sm text-foreground phone:line-clamp-2 phone:whitespace-normal phone:text-[15px] phone:leading-snug"
          title={item.name || 'Untitled task'}
        >
          {item.name || 'Untitled task'}
        </span>
        {isPhone && ((showId && !!taskIdentifier) || (showDue && item.dueDate != null)) && (
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            {showId && taskIdentifier && <span className="font-mono text-[11px] tabular-nums">{taskIdentifier}</span>}
            {showId && taskIdentifier && showDue && item.dueDate != null && <span aria-hidden>·</span>}
            {showDue && item.dueDate != null && (
              <span className={cn('truncate font-medium', getDueDateColorClass(item.dueDate))}>
                {dueText ?? formatDueDate(item.dueDate)}
              </span>
            )}
          </span>
        )}
        {counts && (
          <RowCounts
            counts={counts}
            expandable={expandable}
            expanded={expanded}
            onToggle={() => onToggleExpanded?.(item.id)}
          />
        )}
        {showDescription && (
          <span
            className="min-w-0 flex-1 shrink-[3] truncate text-xs text-muted-foreground"
            title={descriptionPreview}
          >
            {descriptionPreview}
          </span>
        )}
      </div>
      {!isPhone && !isSelecting && !isDragging && (
        <RowHoverActions
          task={item}
          onApply={(updates) => void updateTask(item.id, updates)}
          onPriorityChange={(value) => void handlePriorityChange(value)}
          onToggleSelect={() => toggleBulk(item.id)}
          showAssign={!isPersonalWorkspace}
        />
      )}
      {FEATURE_FLAGS.ENABLE_GITHUB_INTEGRATION && <TaskPrIndicator taskId={item.id} />}
      {/* The ID trails as metadata, like the due date, so titles always start
          right after the status circle and a task without a project has no gap.
          Compact keeps a fixed-width cell so it lines up under its header. */}
      {rowMeta}
      {isPhone ? (
        showPriority && (item.priority ?? 0) > 0 ? (
          <span className="shrink-0 self-start pt-1">
            <PriorityGlyph priority={item.priority ?? 0} />
          </span>
        ) : null
      ) : !showId ? null : (fixedIdColumn || (density === 'compact' && !narrow)) && idColumnCh > 0 ? (
        <span
          className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums"
          style={{ width: `${idColumnCh}ch` }}
          title={taskIdentifier}
        >
          {taskIdentifier}
        </span>
      ) : taskIdentifier ? (
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums" title={taskIdentifier}>
          {taskIdentifier}
        </span>
      ) : null}
      {showDue && !isPhone && (
        <span
          className={cn(
            'text-xs font-medium',
            density === 'compact' && 'w-14 shrink-0 text-left',
            getDueDateColorClass(item.dueDate),
          )}
        >
          {dueText ?? formatDueDate(item.dueDate)}
        </span>
      )}
      {density === 'compact' && !isPhone && (
        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {formatShortAge(item.createdOn)}
        </span>
      )}
      {customFieldColumns && customFieldColumns.definitions.length > 0 && (
        customFieldColumns.definitions.map((def) => {
          const taskValues = customFieldColumns.valueMap.get(item.id)
          const value = taskValues?.get(def.id)
          const options = parseOptions(def.options)
          const entry = FIELD_TYPE_REGISTRY[def.fieldType]
          if (!entry) return null

          const rawValue = value
            ? def.fieldType === 'string' || def.fieldType === 'list'
              ? value.stringValue
              : def.fieldType === 'number'
                ? value.numberValue
                : value.dateValue
            : null

          const { CellRenderer } = entry
          return (
            // Fixed-width columns are dropped in a narrow list pane rather than
            // squeezing the task title, which is the only flexible cell.
            <div
              key={def.id}
              className="hidden w-24 shrink-0 @3xl:flex items-center"
            >
              <CellRenderer definition={def} value={rawValue} options={options} />
            </div>
          )
        })
      )}
      {showIndicatorAfter && (
        <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-0.5 rounded bg-brand" />
      )}
    </motion.li>
  )
}

/**
 * Counts after the title (design 28b): checklist done/total, then sub-tasks
 * done/total. The sub-task count carries the expand chevron, so rows without
 * sub-tasks reserve nothing (design 23). Checklist items are only ever counted.
 */
function RowCounts({
  counts,
  expandable,
  expanded,
  onToggle,
}: {
  counts: TaskRowCounts
  expandable: boolean
  expanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation('tasks')
  if (counts.checklistTotal === 0 && counts.subTaskTotal === 0) return null
  const metric = 'inline-flex items-center gap-1 font-mono text-[11px] tabular-nums text-muted-foreground'
  const subTaskLabel = t('rowCounts.subTasks', { done: counts.subTaskDone, total: counts.subTaskTotal })
  const subTaskContent = (
    <>
      <ListTree className="size-3" aria-hidden />
      {counts.subTaskDone}/{counts.subTaskTotal}
    </>
  )
  return (
    // Above the hover-actions fade, so the sub-task toggle stays visible and
    // clickable when a long title pushes the counts toward the row's end.
    <span className="relative z-[1] flex shrink-0 items-center gap-2.5 self-center">
      {counts.checklistTotal > 0 && (
        <span
          className={metric}
          title={t('rowCounts.checklist', { done: counts.checklistDone, total: counts.checklistTotal })}
        >
          <ListChecks className="size-3" aria-hidden />
          {counts.checklistDone}/{counts.checklistTotal}
        </span>
      )}
      {counts.subTaskTotal > 0 &&
        (expandable ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? t('rowCounts.collapseSubTasks') : t('rowCounts.expandSubTasks')} (${subTaskLabel})`}
            title={subTaskLabel}
            data-testid="subtask-toggle"
            className={cn(
              metric,
              '-mx-1 gap-0.5 rounded px-1 py-0.5 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              expanded && 'text-foreground',
            )}
            onClick={(event) => {
              event.stopPropagation()
              onToggle()
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <ChevronRight className={cn('size-3 transition-transform', expanded && 'rotate-90')} aria-hidden />
            <span className="inline-flex items-center gap-1">{subTaskContent}</span>
          </button>
        ) : (
          <span className={metric} title={subTaskLabel}>
            {subTaskContent}
          </span>
        ))}
    </span>
  )
}

type SubTaskListRowProps = {
  item: EnrichedTask
  index?: number
  offset?: number
  measureElement?: (element: Element | null) => void
  virtualized: boolean
  onSelect?: (task: TaskDocument) => void
  isSelected: boolean
  updateTask: (id: string, updates: Partial<TaskDocument>) => Promise<void>
  statusMode: StatusMode
  taskIdentifier?: string
  idColumnCh: number
  formatDueDate: (value: TaskDocument['dueDate']) => string
  viewMode: 'simple' | 'split'
}

/**
 * A sub-task under its expanded parent (design 28b): a lighter, indented row
 * whose status circle sits under the parent's title. Opens like any row; not
 * draggable, and never a checklist item.
 */
function SubTaskListRow({
  item,
  index,
  offset = 0,
  measureElement,
  virtualized,
  onSelect,
  isSelected,
  updateTask,
  statusMode,
  taskIdentifier,
  idColumnCh,
  formatDueDate,
  viewMode,
}: SubTaskListRowProps) {
  const { t } = useTranslation('tasks')
  const density = useViewModeStore((s) => s.listDensity)
  const hiddenFields = useViewModeStore((s) => s.hiddenRowFields)
  const showId = !hiddenFields.includes('id')
  const showDue = !hiddenFields.includes('due')
  const narrow = viewMode === 'split'
  const isDone = isClosedProgressType(item.status?.progressType)
  const title = item.name || t('untitled')

  const measureElementRef = useRef(measureElement)
  measureElementRef.current = measureElement
  const setRef = useCallback((node: HTMLLIElement | null) => {
    measureElementRef.current?.(node)
  }, [])

  const handleStatusChange = useCallback(
    async (statusId: string) => {
      if (!statusId || statusId === item.statusId) return
      try {
        await updateTask(item.id, { statusId })
      } catch {
        toast.error(t('rowCounts.statusFailed'))
      }
    },
    [item.id, item.statusId, updateTask, t],
  )

  return (
    <motion.li
      data-testid="subtask-item"
      data-task-id={item.id}
      data-index={index}
      ref={setRef}
      className={cn(
        'group relative flex items-center gap-1 border-t border-border/60 pl-[60px] pr-3 transition-colors cursor-pointer @2xl:pl-[64px] @2xl:pr-4',
        'bg-[var(--row-bg)] [--row-bg:color-mix(in_oklab,var(--secondary)_18%,var(--card))] hover:[--row-bg:color-mix(in_oklab,var(--secondary)_45%,var(--card))]',
        density === 'compact' ? 'py-0.5' : 'py-1',
        isSelected &&
          narrow &&
          '[--row-bg:color-mix(in_oklab,var(--brand-tint)_45%,var(--card))] hover:[--row-bg:color-mix(in_oklab,var(--brand-tint)_60%,var(--card))]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
      )}
      style={
        virtualized
          ? { position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${offset}px)`, zIndex: 1 }
          : undefined
      }
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      role="button"
      tabIndex={0}
      aria-selected={isSelected}
      onClick={() => onSelect?.(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect?.(item)
        }
      }}
    >
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <TaskStatusCheckbox
          currentStatusId={item.statusId}
          onStatusChange={handleStatusChange}
          statusMode={statusMode}
          className="h-4 w-4"
        />
      </div>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-[13px]',
          isDone ? 'text-muted-foreground' : 'text-foreground/80',
        )}
        title={title}
      >
        {title}
      </span>
      {!showId ? null : density === 'compact' && !narrow && idColumnCh > 0 ? (
        <span
          className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums"
          style={{ width: `${idColumnCh}ch` }}
          title={taskIdentifier}
        >
          {taskIdentifier}
        </span>
      ) : taskIdentifier ? (
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums" title={taskIdentifier}>
          {taskIdentifier}
        </span>
      ) : null}
      {showDue && (
        <span
          className={cn(
            'text-xs font-medium',
            density === 'compact' && 'w-14 shrink-0 text-left',
            getDueDateColorClass(item.dueDate),
          )}
        >
          {formatDueDate(item.dueDate)}
        </span>
      )}
      {density === 'compact' && (
        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {formatShortAge(item.createdOn)}
        </span>
      )}
    </motion.li>
  )
}

/** Triage on hover (design 13a): schedule, assign, move — without opening the task. */
function RowHoverActions({
  task,
  onApply,
  onPriorityChange,
  onToggleSelect,
  showAssign,
}: {
  task: TaskDocument
  onApply: (updates: Partial<TaskDocument>) => void
  onPriorityChange: (priority: number) => void
  onToggleSelect: () => void
  showAssign: boolean
}) {
  const { t } = useTranslation('tasks')
  const taskId = task.id
  const priority = task.priority ?? 0
  const { deleteTask, updateTask } = useTaskMutations()
  const canDelete = useHasPermission(WORKSPACE_PERMISSIONS.DELETE_TASKS)
  const openMenu = useTaskRowActionsStore((s) => (s.openMenu?.taskId === taskId ? s.openMenu.menu : null))
  const setOpenMenu = useTaskRowActionsStore((s) => s.setOpenMenu)
  const menuProps = (menu: RowMenu) => ({
    open: openMenu === menu,
    onOpenChange: (open: boolean) => setOpenMenu(open ? { taskId, menu } : null),
  })
  // A "more" item that opens another picker can't open it in onSelect: Radix
  // flushes onSelect, then closes the dropdown, whose onOpenChange(false) clears
  // openMenu and unmounts the picker it just showed. Hold the pick until the
  // dropdown has closed, and keep focus off the ⋯ trigger so the picker stays.
  const pendingMenu = useRef<RowMenu | null>(null)
  const openAfterMore = (menu: RowMenu) => () => {
    pendingMenu.current = menu
  }
  const trigger =
    'flex size-[26px] items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground data-[state=open]:bg-secondary data-[state=open]:text-foreground'
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()
  const shortcut = (key: string) => <DropdownMenuShortcut className="font-mono">{key}</DropdownMenuShortcut>

  // Ghost actions over the row's right end, on a fade, covering the ID and
  // date rather than squeezing the title (design 23).
  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-y-0 right-0 flex items-center gap-0.5 bg-gradient-to-l from-[var(--row-bg)] from-60% to-transparent pl-10 pr-2 opacity-0 transition-opacity @2xl:pr-3',
        'group-hover:pointer-events-auto group-hover:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100',
        openMenu && 'pointer-events-auto opacity-100',
      )}
      onClick={stop}
      onKeyDown={stop}
    >
      <TaskRowDatePicker
        task={task}
        {...menuProps('date')}
        trigger={
          <button
            type="button"
            className={trigger}
            aria-label={t('bulk.schedule')}
            title={`${t('bulk.schedule')} (D)`}
            onClick={() => setOpenMenu({ taskId, menu: 'date' })}
          >
            <CalendarDays className="size-3.5" />
          </button>
        }
      />
      {showAssign && (
        <TaskRowAssigneePicker
          task={task}
          onApply={onApply}
          {...menuProps('assign')}
          triggerClassName={trigger}
          icon={<UserPlus className="size-3.5" />}
          label={`${t('bulk.assign')} (A)`}
        />
      )}
      <TaskRowProjectPicker
        task={task}
        onApply={onApply}
        {...menuProps('move')}
        triggerClassName={trigger}
        icon={<FolderInput className="size-3.5" />}
        label={`${t('bulk.move')} (M)`}
      />
      <DropdownMenu {...menuProps('more')}>
        <DropdownMenuTrigger className={trigger} aria-label={t('rowKeys.more')} title={t('rowKeys.more')}>
          <MoreHorizontal className="size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-52"
          onCloseAutoFocus={(e) => {
            const menu = pendingMenu.current
            if (!menu) return
            pendingMenu.current = null
            e.preventDefault()
            setOpenMenu({ taskId, menu })
          }}
        >
          <DropdownMenuItem onSelect={onToggleSelect}>
            {t('rowKeys.select')}
            {shortcut('X')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={openAfterMore('date')}>
            {t('bulk.schedule')}
            {shortcut('D')}
          </DropdownMenuItem>
          {showAssign && (
            <DropdownMenuItem onSelect={openAfterMore('assign')}>
              {t('bulk.assign')}
              {shortcut('A')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={openAfterMore('move')}>
            {t('bulk.move')}
            {shortcut('M')}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>{t('bulk.priority')}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {[4, 3, 2, 1, 0].map((value) => (
                <DropdownMenuItem key={value} onSelect={() => onPriorityChange(value)}>
                  <PriorityGlyph priority={value} />
                  {t(`bulk.priorities.${value}`)}
                  {value === priority && <span className="ml-auto text-xs">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onSelect={() => {
                  void deleteTask(taskId).then(() => {
                    toast(t('rowKeys.deleted'), {
                      action: {
                        label: t('rowKeys.undo'),
                        onClick: () => void updateTask(taskId, { isDeleted: false, deletedOn: null, deletedBy: null }),
                      },
                    })
                  })
                }}
              >
                {t('rowKeys.delete')}
                {shortcut('⌘⌫')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
